import { useEffect, useState } from "react";
import { fetchFlipFlop } from "@/lib/api";
import { Repeat, ArrowRight, CheckCheck } from "lucide-react";

export default function FlipFlopCard({ partyId, partyColor }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        fetchFlipFlop(partyId).then(setData).catch(() => setData(null));
    }, [partyId]);

    if (!data || data.total_topics === 0) return null;

    const score = data.flip_flop_score;
    const level =
        score >= 67 ? { label: "Høj", color: "#BB1B2D" } : score >= 34 ? { label: "Moderat", color: "#C78300" } : { label: "Lav", color: "#186A3B" };

    return (
        <div
            className="bg-white border border-[#E5E8F0] rounded-2xl p-6 md:p-8"
            data-testid={`flipflop-${partyId}`}
        >
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <Repeat className="w-5 h-5 text-royal" />
                        <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                            Flip flop-score
                        </span>
                    </div>
                    <h2 className="font-serif-dm text-3xl text-royal">Har partiet vendt holdninger?</h2>
                    <p className="text-sm text-[#4F5A7A] mt-2 max-w-2xl">
                        Andel af sporede emner hvor partiet har ændret markant holdning over tid.
                    </p>
                </div>
                <div className="text-right min-w-[130px]">
                    <div className="font-serif-dm text-5xl" style={{ color: level.color }} data-testid={`flipflop-score-${partyId}`}>
                        {score}%
                    </div>
                    <div className="text-xs text-[#4F5A7A] mt-1">
                        <strong>{data.shifted_topics}</strong> ud af <strong>{data.total_topics}</strong> emner
                    </div>
                    <span
                        className="inline-block mt-2 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                        style={{ backgroundColor: `${level.color}1A`, color: level.color }}
                    >
                        {level.label} flip flop
                    </span>
                </div>
            </div>

            <div className="space-y-5">
                {data.details.map((d) => (
                    <div
                        key={d.topic}
                        className="border-l-4 rounded-r-lg bg-[#F7F8FC] p-4"
                        style={{ borderColor: d.shifted ? "#BB1B2D" : "#186A3B" }}
                        data-testid={`flipflop-topic-${d.topic}`}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-royal text-lg">{d.topic}</h3>
                            <span
                                className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full`}
                                style={{
                                    backgroundColor: d.shifted ? "#FDEEF0" : "#E8F4EC",
                                    color: d.shifted ? "#BB1B2D" : "#186A3B",
                                }}
                            >
                                {d.shifted ? (
                                    <>
                                        <Repeat className="w-3 h-3" /> Flip flop
                                    </>
                                ) : (
                                    <>
                                        <CheckCheck className="w-3 h-3" /> Konsistent
                                    </>
                                )}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2 items-stretch">
                            {d.snapshots.map((s, i) => (
                                <span
                                    key={`${s.year}-${i}`}
                                    className="inline-flex items-center gap-2"
                                >
                                    <div
                                        className="bg-white border border-[#E5E8F0] rounded-lg px-3 py-2 text-sm max-w-[280px]"
                                    >
                                        <div
                                            className="text-[10px] uppercase tracking-wider font-bold mb-0.5"
                                            style={{ color: partyColor || "#0C2A6B" }}
                                        >
                                            {s.year}
                                        </div>
                                        <div className="text-[#0B1A3A] leading-snug">{s.stance}</div>
                                    </div>
                                    {i < d.snapshots.length - 1 && (
                                        <ArrowRight className="w-4 h-4 text-[#8A94AE] flex-shrink-0" />
                                    )}
                                </span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
